import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import classNames from "classnames";

import { RootState } from "src/redux/reducers";
import { ReactComponent as TickIcon } from "src/images/tick.svg";
import { ReactComponent as EditIcon } from "src/images/edit-gray.svg";
import { Button } from "src/app/components/Button/Button";
import { handleError } from "src/utils/error";
import ProjectsService from "src/services/ProjectsService";
import { SearchHistoryItem } from "./useGetSearchHistory";

interface SearchHistoryPaneListItemProps {
  item: SearchHistoryItem;
  projectId: string;
  editingItemUuid: string | null;
  setEditingItemUuid: (uuid: string | null) => void;
}

function SearchHistoryPaneListItem({
  item,
  projectId,
  editingItemUuid,
  setEditingItemUuid,
}: SearchHistoryPaneListItemProps) {
  const [editedItemName, setEditedItemName] = useState<string>(
    item.projectName ?? item.created,
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const nameBeforeEditRef = useRef<string>(editedItemName);

  const activeWorkspaceUuid = useSelector(
    (state: RootState) => state.mainReducer.activeWorkspaceUuid,
  );

  const isActive = projectId === item.uuid;
  const isEdited = editingItemUuid === item.uuid;
  const isInactive = editingItemUuid !== null && !isEdited;

  const renameProject = async () => {
    if (nameBeforeEditRef.current === editedItemName || !editedItemName) return;

    try {
      await ProjectsService.updateMetaValue({
        metaFieldUuid: item.projectNameMetaFieldUuid,
        metaValue: editedItemName,
        uuids: [item.uuid],
      });
    } catch (err) {
      setEditedItemName(nameBeforeEditRef.current);
      handleError(err);
    }
  };

  const handleBlur = async () => {
    renameProject();
    setEditingItemUuid(null);
  };

  const handleEdit = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (isEdited) {
      setEditingItemUuid(null);
    } else {
      nameBeforeEditRef.current = editedItemName;
      setEditingItemUuid(item.uuid);
    }
  };

  useEffect(() => {
    if (isEdited && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEdited]);

  return (
    <li
      className={classNames("search-history-pane__item", {
        "search-history-pane__item--active": isActive,
        "search-history-pane__item--editing": isEdited,
        "search-history-pane__item--inactive": isInactive,
      })}
    >
      <div className="search-history-pane__row">
        {isActive && <TickIcon />}
        <div className="search-history-pane__row">
          {isEdited && (
            <input
              ref={inputRef}
              type="text"
              name="name"
              value={editedItemName}
              className="search-history-pane__input"
              autoComplete="off"
              onChange={(e) => setEditedItemName(e.target.value)}
              onBlur={handleBlur}
              onKeyDown={(e) => e.key === "Enter" && inputRef.current?.blur()}
            />
          )}
          {!isEdited && isInactive && (
            <span className="search-history-pane__text">{editedItemName}</span>
          )}
          {!isEdited && !isInactive && (
            <Link
              to={`/workspace/${activeWorkspaceUuid}/projects/${item.uuid}`}
              className="search-history-pane__link"
            >
              <span className="search-history-pane__text">
                {editedItemName}
              </span>
            </Link>
          )}
          <Button variant="transparent" onClick={handleEdit}>
            <EditIcon className="search-history-pane__edit-icon" />
          </Button>
        </div>
      </div>
    </li>
  );
}

export default SearchHistoryPaneListItem;
